import { AILog } from "@lib/types/AILog";
import { Drawer, Descriptions, Divider, Typography, Skeleton } from "antd";
import { FormattedMessage, useIntl } from "react-intl";
import {
  CoinPriceTextFormat,
  CustomerName,
  WalletAddressText,
} from "@lib/components/MiniComponents/Others";
import { shortenString } from "@lib/helpers/StringHelper";
import { AppCard, AppJson } from "@lib/components/AppComponents";
import styled from "styled-components";
import dayjs from "dayjs";

const StyledDescriptions = styled(Descriptions)`
  th,
  td {
    padding: 5px !important;
  }
`;

const StyledDrawer = styled(Drawer)`
  .ant-descriptions-item-label {
    width: 200px;
  }
`;

type Props = {
  open: boolean;
  onClose: () => void;
  item: AILog | null;
};

const SkeletonContent = () => (
  <StyledDescriptions bordered column={1}>
    {Array.from({ length: 9 }).map((_, index) => (
      <Descriptions.Item
        key={index}
        label={<Skeleton.Input active size="small" style={{ width: 100 }} />}
      >
        <Skeleton.Input active size="small" style={{ width: 200 }} />
      </Descriptions.Item>
    ))}
  </StyledDescriptions>
);

export const AILogDrawer = (props: Props) => {
  const { open, onClose, item } = props;
  const { messages } = useIntl();

  return (
    <StyledDrawer
      title={
        <FormattedMessage
          id={"common.previewTitle"}
          values={{ title: messages["common.aiLog"] as string }}
        />
      }
      onClose={onClose}
      open={open}
      width={600}
      id="ai-log-drawer"
    >
      {!item ? (
        <SkeletonContent />
      ) : (
        <>
          <StyledDescriptions bordered column={1}>
            <Descriptions.Item label="ID">
              <Typography.Text copyable={{ text: item?.id }}>
                {shortenString(item?.id, 10)}
              </Typography.Text>
            </Descriptions.Item>

            <Descriptions.Item
              label={<FormattedMessage id="common.customer" />}
            >
              <CustomerName customer={item?.customer} link />
            </Descriptions.Item>

            <Descriptions.Item label={<FormattedMessage id="common.task" />}>
              {item?.task}
            </Descriptions.Item>

            <Descriptions.Item label={<FormattedMessage id="common.modelId" />}>
              {item?.modelId}
            </Descriptions.Item>

            <Descriptions.Item label={<FormattedMessage id="common.cost" />}>
              <CoinPriceTextFormat
                amount={item?.cost || 0}
                currency={item?.currency}
                showCurrencySymbol
                showAllDigits
                align="left"
              />
            </Descriptions.Item>

            <Descriptions.Item
              label={<FormattedMessage id="common.actualCost" />}
            >
              <CoinPriceTextFormat
                amount={item?.actualCost || 0}
                currency={item?.currency}
                showCurrencySymbol
                showAllDigits
                align="left"
              />
            </Descriptions.Item>

            <Descriptions.Item
              label={<FormattedMessage id="common.walletAddress" />}
            >
              <WalletAddressText walletAddress={item?.walletAddress} />
            </Descriptions.Item>

            {item?.entityId && (
              <Descriptions.Item
                label={<FormattedMessage id="common.entityId" />}
              >
                {item.entityId}
              </Descriptions.Item>
            )}

            <Descriptions.Item
              label={<FormattedMessage id="common.createdAt" />}
            >
              {item?.createdAt
                ? dayjs(item.createdAt).format("DD.MM.YYYY HH:mm:ss")
                : "-"}
            </Descriptions.Item>
          </StyledDescriptions>

          {item?.extraData && Object.keys(item.extraData).length > 0 && (
            <>
              <Divider />
              <Typography.Title level={5}>
                <FormattedMessage id="common.extraData" />
              </Typography.Title>
              <AppCard borderFull collapse={false}>
                <div style={{ padding: 16 }}>
                  <AppJson src={item.extraData} />
                </div>
              </AppCard>
            </>
          )}
        </>
      )}
    </StyledDrawer>
  );
};
